<script setup lang="ts">
import hljs from 'highlight.js/lib/core'
import pgsql from 'highlight.js/lib/languages/pgsql'
import './assets/style.scss'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

hljs.registerLanguage('pgsql', pgsql)

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    rows?: number | string
    autoGrow?: boolean
    placeholder?: string
    theme?: string
  }>(),
  {
    label: undefined,
    rows: 6,
    autoGrow: false,
    placeholder: undefined,
    theme: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  keydown: [event: KeyboardEvent]
}>()

onMounted(async () => {
  if (props.theme) {
    await import(`highlight.js/styles/${props.theme}.min.css`)
  }
})

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const backdropRef = ref<HTMLPreElement | null>(null)

const model = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
})

const highlighted = computed(() => {
  const code = props.modelValue
  if (!code) return '<br>'
  return hljs.highlight(code, { language: 'pgsql' }).value + '<br>'
})

function syncScroll() {
  if (!textareaRef.value || !backdropRef.value) return
  backdropRef.value.scrollTop = textareaRef.value.scrollTop
  backdropRef.value.scrollLeft = textareaRef.value.scrollLeft
}

function handleInput() {
  if (props.autoGrow && textareaRef.value) {
    textareaRef.value.style.height = 'auto'
    textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`
  }
  nextTick(() => syncScroll())
}

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event)
}

watch(
  () => props.modelValue,
  () => {
    if (props.autoGrow && textareaRef.value) {
      nextTick(() => {
        if (!textareaRef.value) return
        textareaRef.value.style.height = 'auto'
        textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`
      })
    }
  },
)
</script>

<template>
  <div class="v-sql-editor">
    <label v-if="label" class="v-sql-editor__label text-body-2">{{ label }}</label>
    <div class="v-sql-editor__container">
      <pre ref="backdropRef" class="v-sql-editor__backdrop hljs"><code v-html="highlighted"></code></pre>
      <textarea
        ref="textareaRef"
        v-model="model"
        class="v-sql-editor__input"
        :rows="rows"
        :placeholder="placeholder"
        :aria-label="label"
        spellcheck="false"
        @scroll="syncScroll"
        @input="handleInput"
        @keydown="onKeydown"
      />
    </div>
  </div>
</template>
